import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useChatbot } from '../../hooks/useChatbot';
import { useNavigate } from 'react-router-dom';

const MarkdownRenderer = ({ text }: { text: string }) => {
    const html = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.*?)\*/g, '<li>$1</li>')
        .replace(/(<li>.*<\/li>)/gs, '<ul>$1</ul>');

    return <div className="prose prose-sm max-w-none text-inherit [&_*]:text-inherit" dangerouslySetInnerHTML={{ __html: html }} />;
};


interface ChatbotWidgetProps {
    isOpen: boolean;
    onClose: () => void;
}

const ChatbotWidget = ({ isOpen, onClose }: ChatbotWidgetProps) => {
    const { messages, isLoading, sendMessage, navigationPath, clearNavigation } = useChatbot();
    const [input, setInput] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(scrollToBottom, [messages]);

    useEffect(() => {
        if (navigationPath) {
            navigate(navigationPath);
            clearNavigation();
            onClose();
        }
    }, [navigationPath, navigate, clearNavigation, onClose]);

    const handleSend = (e: React.FormEvent) => {
        e.preventDefault();
        if (input.trim()) {
            sendMessage(input);
            setInput('');
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 50, scale: 0.9 }}
                    transition={{ duration: 0.3, ease: 'easeOut' }}
                    className="fixed bottom-24 right-6 w-full max-w-sm h-[60vh] bg-white rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-gray-200"
                >
                    <header className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white flex-shrink-0">
                        <div className="flex items-center gap-3">
                            <i className="ri-robot-line text-2xl"></i>
                            <h3 className="font-bold text-lg">GEC AI Assistant</h3>
                        </div>
                        <button onClick={onClose} className="p-1 rounded-full hover:bg-white/20 transition-colors">
                            <i className="ri-close-line text-2xl"></i>
                        </button>
                    </header>

                    <div className="flex-grow p-4 overflow-y-auto bg-gray-50">
                        <div className="space-y-4">
                             <div className="flex justify-start">
                                <div className="max-w-xs rounded-2xl border border-slate-200 bg-white p-3 text-slate-700 shadow-sm animate-in fade-in slide-in-from-bottom-4 duration-500">
                                   <p className="text-sm">Hello! I'm the GEC AI Assistant. How can I help you plan your study abroad journey?</p>
                                </div>
                            </div>
                            {messages.map((msg, index) => (
                                <div key={index} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                    <div className={`rounded-lg p-3 max-w-xs text-sm ${msg.role === 'user' ? 'bg-gradient-to-br from-blue-600 to-indigo-700 text-white shadow-sm shadow-blue-200 [&_*]:text-white' : 'bg-gray-200 text-gray-800'}`}>
                                        <MarkdownRenderer text={msg.text} />
                                    </div>
                                </div>
                            ))}
                            {isLoading && (
                                <div className="flex justify-start">
                                    <div className="max-w-xs rounded-2xl border border-slate-200 bg-white p-3 text-slate-700 shadow-sm">
                                        <div className="flex items-center gap-2">
                                            <span className="h-2 w-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                                            <span className="h-2 w-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                                            <span className="h-2 w-2 bg-blue-500 rounded-full animate-bounce"></span>
                                        </div>
                                    </div>
                                </div>
                            )}
                             <div ref={messagesEndRef} />
                        </div>
                    </div>

                    <footer className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
                        <form onSubmit={handleSend} className="flex items-center gap-3">
                            <input
                                type="text"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder="Ask about countries, courses..."
                                className="w-full px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                            <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white rounded-full w-10 h-10 flex-shrink-0 flex items-center justify-center transition-colors disabled:bg-gray-400" disabled={isLoading || !input.trim()}>
                                <i className="ri-send-plane-fill"></i>
                            </button>
                        </form>
                    </footer>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export default ChatbotWidget;